import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { Edit as EditIcon, Search as SearchIcon } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SubtaskItemStatusLabel } from '../../../../constants';
import { ISubtaskItem } from '../../../../models/subtaskItem.model';
import { ISubtaskType } from '../../../../models/subtaskType.model';
import { AppState } from '../../../../state/configureStore';
import { SubtaskState } from '../../../../state/subtask';
import {
  SubtaskItemState,
  updateSubtaskItemCustReqNo,
} from '../../../../state/subtaskItem';
import { SubtaskTypeState } from '../../../../state/subtaskType';
import {
  MultipleSubtaskItemForecastEditDialog,
  SubtaskItemForecastTable,
} from '../../../components/Forecast';
import {
  MultipleSubtaskItemMoqEditDialog,
  SubtaskItemMoqTable,
} from '../../../components/Moq';
import SubtaskItemActionMenu from './SubtaskItemActionMenu';
import SubtaskItemArtTemplateTable from './SubtaskItemArtTemplateTable';
import SubtaskItemEditStatusDialog from './SubtaskItemEditStatusDialog';
import SubtaskItemTimelineDialog from './SubtaskItemTimelineDialog';
import { Timer as TimerIcon } from '@material-ui/icons';
import SaveIcon from '@material-ui/icons/Save';
import {
  SubtaskItemChangeStatusLogState,
  fetchSubtaskItemChangeStatusLogList,
} from '../../../../state/subtaskItemChangeStatusLog';
import { SubtaskStatus } from '../../../../constants/subtaskType';
import useStyles from './subtaskList.style';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { searchCRTNumber } from '../../../../services/crtService';

interface CustomerRequirementOption {
  id: string;
  customerCode: string;
  customerName: string;
  program: string;
  brandName: string;
}

const customerRequirementOptions: CustomerRequirementOption[] = [
  {
    id: 'REQ-260400197',
    customerCode: '\u0e08157',
    customerName: '\u0e40\u0e21\u0e22\u0e4c',
    program: 'CP(AL)',
    brandName: 'EZYGO GOLDSELECTION',
  },
  {
    id: 'REQ-260400198',
    customerCode: '\u0e08157',
    customerName: '\u0e40\u0e21\u0e22\u0e4c',
    program: 'CP(AL)',
    brandName: 'EZYGO GOLDSELECTION',
  },
  {
    id: 'REQ-260800199',
    customerCode: '\u0e08157',
    customerName: '\u0e40\u0e21\u0e22\u0e4c',
    program: 'CP(AL)',
    brandName: 'EZYGO GOLDSELECTION',
  },
];

interface CustomerRequirementSearchDialogProps {
  open: boolean;
  company: string;
  program: string;
  crtNumber: string;
  options: CustomerRequirementOption[];
  onClose: () => void;
  onSelect: (option: CustomerRequirementOption) => void;
}

const CustomerRequirementSearchDialog: React.FC<CustomerRequirementSearchDialogProps> = (
  props,
) => {
  const { open, company, program, crtNumber, options, onClose, onSelect } = props;
  const [companyFilter, setCompanyFilter] = useState(company);
  const [programFilter, setProgramFilter] = useState(program);
  const [crtNumberFilter, setCrtNumberFilter] = useState(crtNumber);
  const [selectedOptionId, setSelectedOptionId] = useState(crtNumber);
  const [filteredOptions, setFilteredOptions] = useState<CustomerRequirementOption[]>(
    options,
  );
  const [isSearching, setIsSearching] = useState(false);
  const isCompanyRequired = companyFilter.trim() === '';
  const isProgramRequired = programFilter.trim() === '';
  const isCrtNumberRequired = crtNumberFilter.trim() === '';
  const isSearchDisabled =
    isSearching || isCompanyRequired || isProgramRequired || isCrtNumberRequired;

  const handleSearch = async (
    nextCompany: string,
    nextProgram: string,
    nextCrtNumber: string,
  ) => {
    if (
      nextCompany.trim() === '' ||
      nextProgram.trim() === '' ||
      nextCrtNumber.trim() === ''
    ) {
      return;
    }

    setIsSearching(true);
    try {
      const { data } = await searchCRTNumber(nextCompany, nextProgram, nextCrtNumber);
      const result = Array.isArray(data) ? data : data?.items ?? [];
      setFilteredOptions(result);
    } catch (error) {
      setFilteredOptions(options);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (open) {
      setCompanyFilter(company);
      setProgramFilter(program);
      setCrtNumberFilter(crtNumber);
      setSelectedOptionId(crtNumber);
      if (company.trim() !== '' && program.trim() !== '' && crtNumber.trim() !== '') {
        void handleSearch(company, program, crtNumber);
      } else {
        setFilteredOptions(options);
      }
    }
  }, [open, company, program, crtNumber]);

  const selectedOption = filteredOptions.find((option) => option.id === selectedOptionId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Search Customer Requirement</DialogTitle>
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12} md={3}>
            <TextField
              label="Company"
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth
              required
              error={isCompanyRequired}
              helperText={isCompanyRequired ? 'Company is required' : ''}
              value={companyFilter}
              onChange={(event) => {
                setCompanyFilter(event.target.value);
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Program"
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth
              required
              error={isProgramRequired}
              helperText={isProgramRequired ? 'Program is required' : ''}
              value={programFilter}
              onChange={(event) => {
                setProgramFilter(event.target.value);
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="CRT Number"
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth
              required
              error={isCrtNumberRequired}
              helperText={isCrtNumberRequired ? 'CRT Number is required' : ''}
              value={crtNumberFilter}
              onChange={(event) => {
                setCrtNumberFilter(event.target.value);
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              color="primary"
              variant="contained"
              fullWidth
              disabled={isSearchDisabled}
              onClick={() => {
                void handleSearch(companyFilter, programFilter, crtNumberFilter);
              }}
              style={{ marginTop: 8 }}
            >
              Search
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Select</TableCell>
                  <TableCell>CRT Number</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Program</TableCell>
                  <TableCell>Brand</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isSearching && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                )}
                {!isSearching &&
                  filteredOptions.map((option) => (
                    <TableRow key={option.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedOptionId === option.id}
                          onChange={(event) => {
                            setSelectedOptionId(event.target.checked ? option.id : '');
                          }}
                        />
                      </TableCell>
                      <TableCell>{option.id}</TableCell>
                      <TableCell>
                        {option.customerCode + ' ' + option.customerName}
                      </TableCell>
                      <TableCell>{option.program}</TableCell>
                      <TableCell>{option.brandName}</TableCell>
                    </TableRow>
                  ))}
                {!isSearching && filteredOptions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No data
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!selectedOption || isSearching}
          onClick={() => {
            if (selectedOption) {
              onSelect(selectedOption);
            }
          }}
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SubtaskItemTable: React.FC = () => {
  const classes = useStyles();
  const [openSubtaskEditStatusDialog, setOpenSubtaskEditStatusDialog] = useState<boolean>(
    false,
  );
  const [
    openMultipleSubtaskItemMoqEditDialog,
    setOpenMultipleSubtaskItemMoqEditDialog,
  ] = useState<boolean>(false);
  const [
    openMultipleSubtaskItemForecastEditDialog,
    setOpenMultipleSubtaskItemForecastEditDialog,
  ] = useState<boolean>(false);

  const [openTimelineDialog, setOpenTimelineDialog] = useState<boolean>(false);
  const [selectedSubtaskItemId, setSelectedSubtaskItemId] = useState<number>(0);
  const [
    openCustomerRequirementSearchDialog,
    setOpenCustomerRequirementSearchDialog,
  ] = useState<boolean>(false);
  const [
    selectedCustomerRequirementSubtaskItem,
    setSelectedCustomerRequirementSubtaskItem,
  ] = useState<ISubtaskItem | null>(null);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedSubtaskItems, setSelectedSubtaskItems] = useState<
    ReadonlyArray<ISubtaskItem>
  >([]);
  const noSelectedSubtaskItems = selectedSubtaskItems.length === 0;
  const { items: subtaskItemList } = useSelector<AppState, SubtaskItemState>(
    (state) => state.subtaskItem,
  );
  const [
    selectedSubtaskItemOnTable,
    setSelectedSubtaskItemOnTable,
  ] = useState<ISubtaskItem>({});
  const { item: subtask } = useSelector<AppState, SubtaskState>((state) => state.subtask);
  const { items: subtaskTypeList } = useSelector<AppState, SubtaskTypeState>(
    (state) => state.subtaskType,
  );
  const findSubtaskTypePrice = subtaskTypeList.find(
    (subtaskType: ISubtaskType) => subtaskType.code === 'PRICE',
  );

  const findSubtaskTypeArtwork = subtaskTypeList.find(
    (subtaskType: ISubtaskType) => subtaskType.code === 'ARTWORK',
  );

  const isSubtaskTypePrice = findSubtaskTypePrice
    ? subtask.smSubtaskTypeId === findSubtaskTypePrice.id
    : false;

  const isSubtaskTypeArtwork = findSubtaskTypeArtwork
    ? subtask.smSubtaskTypeId === findSubtaskTypeArtwork.id
    : false;

  const [isEBlock, setIsEBlock] = useState(false);

  const noSelectedSubtaskItemsOnTable =
    Object.keys(selectedSubtaskItemOnTable).length === 0 &&
    selectedSubtaskItemOnTable.constructor === Object;

  useEffect(() => {
    const newSelectedSubtaskItemOnTable = subtaskItemList.find(
      (subtaskItem) => subtaskItem.id === selectedSubtaskItemOnTable.id,
    );
    if (newSelectedSubtaskItemOnTable) {
      setSelectedSubtaskItemOnTable(newSelectedSubtaskItemOnTable);
    }
  }, [subtaskItemList, selectedSubtaskItemOnTable]);

  const [subtaskItemDto, setSubtaskItemDto] = useState<ISubtaskItem[]>([]);

  useEffect(() => {
    if (subtaskItemList.length > 0) {
      setSubtaskItemDto(subtaskItemList);
    }
  }, [subtaskItemList]);

  const { loading: logsLoading } = useSelector<AppState, SubtaskItemChangeStatusLogState>(
    (state) => state.subtaskItemChangeStatusLog,
  );

  const dispatch = useDispatch();
  useEffect(() => {
    if (selectedSubtaskItemOnTable.smSubtaskId !== undefined) {
      dispatch(
        fetchSubtaskItemChangeStatusLogList(
          { 'SmSubtaskItemId.EqualsTo': selectedSubtaskItemOnTable.id },
          {
            pageNumber: 0,
            pageSize: 999,
          },
        ),
      );
    }
    if (selectedSubtaskItemOnTable.status !== undefined) {
      console.log('=====> select status ==>', selectedSubtaskItemOnTable.status);

      const index = steps.findIndex(
        (f) => f === SubtaskItemStatusLabel[selectedSubtaskItemOnTable.status ?? ''],
      );
      setActiveStep(index);
    }
  }, [selectedSubtaskItemOnTable]);

  // const steps = [
  //   'Pending',
  //   'In Progress',
  //   'Waiting NPD',
  //   'Approve By NPD',
  //   'Waiting Customer',
  //   'Done',
  // ];

  const [steps, setSteps] = useState<string[]>([]);
  const [activeStep, setActiveStep] = useState<number>(0);

  const isOpenStepper =
    !subtask.useCommonStatus && !noSelectedSubtaskItemsOnTable && !logsLoading;

  useEffect(() => {
    // if (subtask.smSubtaskType?.code === 'PROOF') {
    //   setSteps(SubtaskStatus.proof);
    // } else if (subtask.smSubtaskType?.code === 'MOCKUP') {
    //   setSteps(SubtaskStatus.mockup);
    // } else if (subtask.smSubtaskType?.code === 'ARTWORK') {
    //   setSteps(SubtaskStatus.artwork);
    // }

    if (subtask.smSubtaskType?.code === 'PROOF') {
      setSteps(SubtaskStatus.proof);
    } else if (subtask.smSubtaskType?.code === 'MOCKUP') {
      setSteps(SubtaskStatus.mockup);
    } else if (subtask.smSubtaskType?.code === 'VERIFY DATA') {
      setSteps(SubtaskStatus.verifyData);
    } else {
      setSteps(SubtaskStatus.artwork);
    }
  }, [subtask]);

  return (
    <Box>
      <Box className={classes.stickyActionBar}>
        <Button
          color="primary"
          variant="contained"
          className={classes.actionToolbarButton}
          onClick={() => {
            setOpenSubtaskEditStatusDialog(true);
          }}
          startIcon={<EditIcon />}
          disabled={noSelectedSubtaskItems}
        >
          {`Change Status`}
        </Button>
        {isSubtaskTypePrice && (
          <>
            <Button
              color="primary"
              variant="contained"
              className={classes.actionToolbarButton}
              onClick={() => {
                setOpenMultipleSubtaskItemMoqEditDialog(true);
              }}
              startIcon={<EditIcon />}
              disabled={noSelectedSubtaskItems}
            >
              {`Change MOQ`}
            </Button>
            <Button
              color="primary"
              variant="contained"
              className={classes.actionToolbarButton}
              onClick={() => {
                setOpenMultipleSubtaskItemForecastEditDialog(true);
              }}
              startIcon={<EditIcon />}
              disabled={noSelectedSubtaskItems}
            >
              {`Change Forecast`}
            </Button>
          </>
        )}
      </Box>
      <Grid container spacing={2} className={classes.sectionGrid}>
        <Grid item xs={12} lg={7}>
          <Card className={classes.tablePanel} elevation={0}>
            <CardContent className={classes.tablePanelContent}>
              <Box className={classes.tableHeader}>
                <Box>
                  <Typography variant="h6" className={classes.tableTitle}>
                    Subtask Items
                  </Typography>
                  <Typography variant="body2" className={classes.tableSubtitle}>
                    Review line items, update notes, and open detailed timelines from one
                    focused workspace.
                  </Typography>
                </Box>
                <Box
                  className={classes.tableBadge}
                >{`${subtaskItemDto.length} items`}</Box>
              </Box>

              <Box className={classes.subtaskTableScroller}>
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow className={classes.headerRow}>
                      <TableCell
                        padding="checkbox"
                        className={`${classes.headerCell} ${classes.headerCheckboxCell}`}
                      >
                        <Checkbox
                          className={classes.tableCheckbox}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                            checked,
                          ) => {
                            setSelectAll(checked);
                            if (checked) {
                              setSelectedSubtaskItems([...subtaskItemList]);
                            } else {
                              setSelectedSubtaskItems([]);
                            }
                          }}
                          checked={selectAll}
                        />
                      </TableCell>
                      {/* <TableCell className={classes.headerCell}>No.</TableCell> */}
                      <TableCell className={classes.headerCell}>Block</TableCell>
                      {/* <TableCell className={classes.headerCell}>Program</TableCell> */}
                      <TableCell className={classes.headerCell}>Item</TableCell>
                      {/* {isSubtaskTypeArtwork && ( */}
                      <TableCell className={classes.headerCell}>
                        Customer Requirement
                      </TableCell>
                      {/* )} */}
                      <TableCell className={classes.headerCell}>Note</TableCell>
                      <TableCell className={classes.headerCell}>Status</TableCell>
                      <TableCell
                        className={classes.headerCell}
                        style={{ minWidth: 90, maxWidth: 90 }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subtaskItemDto.map((subtaskItem, index) => {
                      const isCheckboxChecked =
                        selectedSubtaskItems.findIndex(
                          (item) => item.id === subtaskItem.id,
                        ) !== -1;
                      return (
                        <TableRow
                          key={subtaskItem.id}
                          className={`${classes.dataRow} ${
                            selectedSubtaskItemOnTable.id === subtaskItem.id
                              ? classes.selectedRow
                              : ''
                          }`}
                          onClick={() => {
                            setSelectedSubtaskItemOnTable(subtaskItem);
                            setIsEBlock(
                              subtaskItem.smTaskItem?.opportunityItem?.block?.isEBlock ??
                                false,
                            );
                          }}
                          selected={selectedSubtaskItemOnTable.id === subtaskItem.id}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              className={classes.tableCheckbox}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                                checked,
                              ) => {
                                if (checked) {
                                  setSelectedSubtaskItems(
                                    selectedSubtaskItems.concat([subtaskItem]),
                                  );
                                } else {
                                  setSelectAll(false);
                                  setSelectedSubtaskItems(
                                    selectedSubtaskItems.filter(
                                      (item) => item.id !== subtaskItem.id,
                                    ),
                                  );
                                }
                              }}
                              checked={isCheckboxChecked}
                            />
                          </TableCell>
                          {/* <TableCell>
                            <span className={classes.indexBadge}>{index + 1}</span>
                          </TableCell> */}
                          <TableCell>
                            <Typography variant="body2">
                              {subtaskItem.smTaskItem?.opportunityItem?.block?.blockNo ??
                                ''}
                            </Typography>
                          </TableCell>
                          {/* <TableCell>
                            <Typography className={classes.rowSecondaryText}>
                              {subtaskItem.smTaskItem?.opportunityItem?.opportunity
                                ?.program?.name ?? ''}
                            </Typography>
                          </TableCell> */}
                          <TableCell>
                            <Typography variant="body2">
                              {subtaskItem.smTaskItem?.opportunityItem?.block?.item ?? ''}
                            </Typography>
                          </TableCell>
                          {/* CRT Number */}
                          <TableCell style={{ minWidth: 120, maxWidth: 200 }}>
                            <TextField
                              variant="outlined"
                              size="small"
                              value={subtaskItem.custReqNo ?? ''}
                              onChange={(e) => {
                                const newSubtaskItem = {
                                  ...subtaskItem,
                                  custReqNo: e.target.value,
                                };

                                const newSubtaskItems = subtaskItemDto.map((item) => {
                                  return item.id === subtaskItem.id
                                    ? newSubtaskItem
                                    : item;
                                });
                                setSubtaskItemDto(newSubtaskItems);
                              }}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      size="small"
                                      onClick={(event) => {
                                        console.log(
                                          '=== selected subtask item ===',
                                          subtaskItem,
                                        );

                                        event.stopPropagation();
                                        setSelectedCustomerRequirementSubtaskItem(
                                          subtaskItem,
                                        );
                                        setOpenCustomerRequirementSearchDialog(true);
                                      }}
                                    >
                                      <SearchIcon color="primary" />
                                    </IconButton>
                                    {/* <IconButton
                                      size="small"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        const currentRow = subtaskItemDto.find(
                                          (f) => f.id === subtaskItem.id,
                                        );
                                        if (currentRow) {
                                          dispatch(
                                            updateSubtaskItemCustReqNo(currentRow),
                                          );
                                        }
                                      }}
                                    >
                                      <CheckCircleOutlineIcon color="primary" />
                                    </IconButton> */}
                                  </InputAdornment>
                                ),
                              }}
                              placeholder="กรอก CRT Number"
                            />
                          </TableCell>
                          {/* )} */}
                          <TableCell style={{ minWidth: 120, maxWidth: 200 }}>
                            <TextField
                              size="small"
                              variant="outlined"
                              fullWidth
                              value={subtaskItem.note ?? ''}
                              onChange={(e) => {
                                const newSubtaskItem = {
                                  ...subtaskItem,
                                  note: e.target.value,
                                };

                                const newSubtaskItems = subtaskItemList.map((item) => {
                                  return item.id === subtaskItem.id
                                    ? newSubtaskItem
                                    : item;
                                });
                                setSubtaskItemDto(newSubtaskItems);
                              }}
                              // InputProps={{
                              //   endAdornment: (
                              //     <InputAdornment
                              //       position="end"
                              //       style={{ cursor: 'pointer' }}
                              //     >
                              //       <CheckCircleOutlineIcon
                              //         color="primary"
                              //         onClick={() => {
                              //           const currentRow = subtaskItemDto.find(
                              //             (f) => f.id === subtaskItem.id,
                              //           );
                              //           if (currentRow) {
                              //             dispatch(
                              //               updateSubtaskItemCustReqNo(currentRow),
                              //             );
                              //           }
                              //         }}
                              //       />
                              //     </InputAdornment>
                              //   ),
                              // }}
                            />
                          </TableCell>
                          <TableCell>
                            <span className={classes.statusPill}>
                              {SubtaskItemStatusLabel[subtaskItem.status ?? '']}
                            </span>
                          </TableCell>
                          <TableCell>
                            {/* <Button
                              color="default"
                              size="small"
                              variant="contained"
                              className={classes.timelineButton}
                              onClick={() => {
                                setSelectedSubtaskItemId(subtaskItem.id ?? 0);
                                setOpenTimelineDialog(true);
                              }}
                              startIcon={<TimerIcon />}
                            >
                              Open
                            </Button> */}
                            <IconButton
                              color="primary"
                              aria-label="Save"
                              size="small"
                              onClick={() => {
                                const currentRow = subtaskItemDto.find(
                                  (f) => f.id === subtaskItem.id,
                                );
                                if (currentRow) {
                                  dispatch(updateSubtaskItemCustReqNo(currentRow));
                                }
                              }}
                            >
                              <SaveIcon />
                            </IconButton>
                            <IconButton
                              aria-label="timeline"
                              size="small"
                              onClick={() => {
                                setSelectedSubtaskItemId(subtaskItem.id ?? 0);
                                setOpenTimelineDialog(true);
                              }}
                            >
                              <TimerIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Grid container spacing={0} className={classes.detailColumn}>
            <Grid item xs={12}>
              {isOpenStepper && (
                <Card className={classes.stepperCard} elevation={0}>
                  <CardContent className={classes.stepperCardContent}>
                    <Box className={classes.stepperHeader}>
                      <Box>
                        <Typography variant="h6" className={classes.stepperTitle}>
                          Change Status Item
                        </Typography>
                        <Typography variant="body2" className={classes.stepperSubtitle}>
                          Review the current workflow stage before updating this item.
                        </Typography>
                      </Box>
                      <Box className={classes.stepperBadge}>
                        {`Step ${Math.max(activeStep + 1, 1)}/${steps.length || 1}`}
                      </Box>
                    </Box>

                    <Box className={classes.stepperTrack}>
                      <Stepper
                        activeStep={activeStep}
                        alternativeLabel
                        className={classes.stepperRoot}
                      >
                        {steps.map((label, index) => {
                          const stepProps: { completed?: boolean } = {};
                          stepProps.completed = index < activeStep;

                          return (
                            <Step key={label} {...stepProps}>
                              <StepLabel
                                StepIconProps={{
                                  classes: {
                                    root: classes.stepIconRoot,
                                    active: classes.stepIconActive,
                                    completed: classes.stepIconCompleted,
                                    text: classes.stepIconText,
                                  },
                                }}
                              >
                                <span className={classes.stepLabelText}>{label}</span>
                              </StepLabel>
                            </Step>
                          );
                        })}
                      </Stepper>
                    </Box>

                    <Box className={classes.stepperItemPanel}>
                      <Typography className={classes.stepperItemLabel}>
                        Selected Item
                      </Typography>
                      <Typography variant="body1" className={classes.stepperItemValue}>
                        {
                          selectedSubtaskItemOnTable.smTaskItem?.opportunityItem?.block
                            ?.item
                        }
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Grid>
            <Grid item xs={12}>
              <Box mt={2}>
                {!noSelectedSubtaskItemsOnTable && (
                  <>
                    <Box m={1}>
                      <SubtaskItemActionMenu subtaskItem={selectedSubtaskItemOnTable} />
                    </Box>
                    {isSubtaskTypeArtwork && isEBlock && (
                      <>
                        <Box m={1}>
                          <Card className={classes.infoCard} elevation={0}>
                            <CardContent className={classes.infoCardContent}>
                              <SubtaskItemArtTemplateTable
                                subtaskItem={selectedSubtaskItemOnTable}
                              />
                            </CardContent>
                          </Card>
                        </Box>
                      </>
                    )}
                    {isSubtaskTypePrice && (
                      <>
                        <Box m={1}>
                          <Card className={classes.infoCard} elevation={0}>
                            <CardContent className={classes.infoCardContent}>
                              <SubtaskItemForecastTable
                                subtaskItem={selectedSubtaskItemOnTable}
                              />
                            </CardContent>
                          </Card>
                        </Box>
                        <Box m={1}>
                          <Card className={classes.infoCard} elevation={0}>
                            <CardContent className={classes.infoCardContent}>
                              <SubtaskItemMoqTable
                                subtaskItem={selectedSubtaskItemOnTable}
                              />
                            </CardContent>
                          </Card>
                        </Box>
                      </>
                    )}
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <SubtaskItemEditStatusDialog
        onClose={() => {
          setOpenSubtaskEditStatusDialog(false);
          setSelectedSubtaskItems([]);
          setSelectAll(false);
        }}
        open={openSubtaskEditStatusDialog}
        subtaskItems={selectedSubtaskItems}
      />
      <MultipleSubtaskItemMoqEditDialog
        open={openMultipleSubtaskItemMoqEditDialog}
        subtaskItems={selectedSubtaskItems}
        onClose={() => {
          setOpenMultipleSubtaskItemMoqEditDialog(false);
          setSelectedSubtaskItems([]);
          setSelectAll(false);
        }}
        selectedSubtaskItemOnTable={selectedSubtaskItemOnTable}
      />
      <MultipleSubtaskItemForecastEditDialog
        open={openMultipleSubtaskItemForecastEditDialog}
        subtaskItems={selectedSubtaskItems}
        onClose={() => {
          setOpenMultipleSubtaskItemForecastEditDialog(false);
          setSelectedSubtaskItems([]);
          setSelectAll(false);
        }}
        selectedSubtaskItemOnTable={selectedSubtaskItemOnTable}
      />

      <CustomerRequirementSearchDialog
        open={openCustomerRequirementSearchDialog}
        company={
          selectedCustomerRequirementSubtaskItem?.smTaskItem?.opportunityItem?.opportunity
            ?.company?.externalId ?? ''
        }
        program={
          selectedCustomerRequirementSubtaskItem?.smTaskItem?.opportunityItem?.opportunity
            ?.program?.name ?? ''
        }
        crtNumber={`REQ-${new Date().getFullYear().toString().slice(-2)}`}
        options={customerRequirementOptions}
        onClose={() => {
          setOpenCustomerRequirementSearchDialog(false);
          setSelectedCustomerRequirementSubtaskItem(null);
        }}
        onSelect={(option) => {
          if (selectedCustomerRequirementSubtaskItem) {
            const newSubtaskItems = subtaskItemDto.map((item) => {
              return item.id === selectedCustomerRequirementSubtaskItem.id
                ? { ...item, custReqNo: option.id }
                : item;
            });
            setSubtaskItemDto(newSubtaskItems);
          }
          setOpenCustomerRequirementSearchDialog(false);
          setSelectedCustomerRequirementSubtaskItem(null);
        }}
      />

      <SubtaskItemTimelineDialog
        open={openTimelineDialog}
        subtaskItemId={selectedSubtaskItemId}
        handleClose={() => {
          setOpenTimelineDialog(false);
        }}
      ></SubtaskItemTimelineDialog>
    </Box>
  );
};

export default SubtaskItemTable;
